import React, { useState, useRef, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { getVisitas, getReports, escanearQR, buscarVisitas, createReport } from '../api';
import Header from '../components/Header';
import QRScanner from '../components/QRScanner';
import VisitItem from '../components/VisitItem';
import { storage } from '../firebase';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { translateError } from '../utils';

export default function GuardPanel() {
  const { user } = useAuth();
  const [view, setView] = useState('menu');
  const [visitas, setVisitas] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [escaneoResult, setEscaneoResult] = useState(null);

  // Paso actual después de escanear QR: 'info' | 'placa' | 'cedula' | 'confirmar'
  const [paso, setPaso] = useState('info');
  const [fotoPlaca, setFotoPlaca] = useState(null);
  const [fotoCedula, setFotoCedula] = useState(null);

  const [searchCedula, setSearchCedula] = useState('');
  const [reportesMap, setReportesMap] = useState({});

  // Cargar reportes para mapear visitaId -> reportes
  const loadReportesMap = async () => {
    try {
      const reps = await getReports();
      const map = {};
      reps.forEach(r => {
        if (r.visitaId) {
          if (!map[r.visitaId]) map[r.visitaId] = [];
          map[r.visitaId].push(r);
        }
      });
      setReportesMap(map);
    } catch (e) { /* silencioso */ }
  };

  // Reporte
  const [showReportModal, setShowReportModal] = useState(false);
  const [reportVisita, setReportVisita] = useState(null);
  const [reportText, setReportText] = useState('');
  const [reportFotos, setReportFotos] = useState([]);
  const [reportLoading, setReportLoading] = useState(false);
  const reportFileRef = useRef(null);

  const openReport = (visita) => {
    setReportVisita(visita);
    setReportText('');
    setReportFotos([]);
    setShowReportModal(true);
  };

  const handleReportFoto = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => setReportFotos([...reportFotos, reader.result]);
      reader.readAsDataURL(file);
    }
  };

  const submitReport = async () => {
    if (!reportText.trim() && reportFotos.length === 0) {
      setError('Escribe un texto o adjunta al menos una foto');
      return;
    }
    setReportLoading(true); setError('');
    try {
      // Subir fotos del reporte a Storage
      const fotoUrls = [];
      for (const foto of reportFotos) {
        const ts = Date.now();
        const reportRef = ref(storage, `reportes/${reportVisita.id}_${ts}.jpg`);
        const blob = await fetch(foto).then(r => r.blob());
        const snap = await uploadBytes(reportRef, blob);
        const url = await getDownloadURL(snap.ref);
        fotoUrls.push(url);
      }
      await createReport({
        visitaId: reportVisita.id,
        vigilante_nombre: user.nombre_completo,
        residenteId: reportVisita.residenteId,
        texto: reportText,
        fotos: fotoUrls,
        categoria: 'anomalia',
        tipo: 'vigilancia'
      });
      setShowReportModal(false);
      setReportVisita(null);
    } catch (err) {
      setError(translateError(err));
    }
    setReportLoading(false);
  };

  // Cámara en vivo
  const [showCamera, setShowCamera] = useState(false);
  const [cameraTarget, setCameraTarget] = useState(null); // 'placa' | 'cedula'
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const canvasRef = useRef(null);

  const openCamera = useCallback(async (target) => {
    setCameraTarget(target);
    setShowCamera(true);
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: 'environment', width: { ideal: 1280 }, height: { ideal: 720 } }
      });
      streamRef.current = stream;
      // Esperar a que el video esté en el DOM
      setTimeout(() => {
        if (videoRef.current) {
          videoRef.current.srcObject = stream;
        }
      }, 100);
    } catch (err) {
      setShowCamera(false);
      setError('No se pudo acceder a la cámara. Verifica los permisos.');
    }
  }, []);

  const capturePhoto = useCallback(() => {
    if (!videoRef.current || !canvasRef.current) return;
    const video = videoRef.current;
    const canvas = canvasRef.current;
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    const ctx = canvas.getContext('2d');
    ctx.drawImage(video, 0, 0);
    const dataUrl = canvas.toDataURL('image/jpeg', 0.85);
    
    if (cameraTarget === 'placa') {
      setFotoPlaca(dataUrl);
    } else {
      setFotoCedula(dataUrl);
    }
    
    // Cerrar cámara
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(t => t.stop());
      streamRef.current = null;
    }
    setShowCamera(false);
    setCameraTarget(null);
  }, [cameraTarget]);

  const closeCamera = useCallback(() => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(t => t.stop());
      streamRef.current = null;
    }
    setShowCamera(false);
    setCameraTarget(null);
  }, []);

  const loadVisitas = async () => {
    setLoading(true); setError('');
    try {
      const [data, reps] = await Promise.all([getVisitas(), getReports()]);
      setVisitas(data);
      const map = {};
      reps.forEach(r => {
        if (r.visitaId) {
          if (!map[r.visitaId]) map[r.visitaId] = [];
          map[r.visitaId].push(r);
        }
      });
      setReportesMap(map);
    } catch (err) { setError(translateError(err)); }
    setLoading(false);
  };

  const handleSearch = async (e) => {
    e.preventDefault();
    if (!searchCedula.trim()) return;
    setLoading(true);
    try { const data = await buscarVisitas(searchCedula.trim()); setVisitas(data); } catch (err) { setError(translateError(err)); }
    setLoading(false);
  };

  const handleScanResult = (qrText) => {
    setEscaneoResult(qrText);
    setPaso('info');
    setFotoPlaca(null);
    setFotoCedula(null);
  };

  const confirmarVisita = async () => {
    if (!escaneoResult) return;
    setLoading(true); setError('');
    try {
      // Subir fotos si están presentes
      let placaUrl = null, cedulaUrl = null;
      
      if (fotoPlaca) {
        const timestamp = Date.now();
        const visitanteId = escaneoResult.visitantes?.[0]?.cedula || escaneoResult.visitante_cedula || 'unknown';
        const placaRef = ref(storage, `visitas/placas/${visitanteId}_${timestamp}.jpg`);
        const placaBlob = await fetch(fotoPlaca).then(r => r.blob());
        const placaSnapshot = await uploadBytes(placaRef, placaBlob);
        placaUrl = await getDownloadURL(placaSnapshot.ref);
      }
      
      if (fotoCedula) {
        const timestamp = Date.now();
        const visitanteId = escaneoResult.visitantes?.[0]?.cedula || escaneoResult.visitante_cedula || 'unknown';
        const cedulaRef = ref(storage, `visitas/cedulas/${visitanteId}_${timestamp}.jpg`);
        const cedulaBlob = await fetch(fotoCedula).then(r => r.blob());
        const cedulaSnapshot = await uploadBytes(cedulaRef, cedulaBlob);
        cedulaUrl = await getDownloadURL(cedulaSnapshot.ref);
      }
      
      // Registrar la visita incluyendo URLs de fotos
      const visitaData = {
        ...escaneoResult,
        foto_placa_url: placaUrl,
        foto_cedula_url: cedulaUrl
      };
      
      const data = await escanearQR(visitaData);
      setEscaneoResult({ ...escaneoResult, success: data.message });
      setPaso('confirmar');
    } catch (err) {
      setError(translateError(err));
    }
    setLoading(false);
  };

  const resetEscanear = () => {
    setEscaneoResult(null);
    setPaso('info');
    setFotoPlaca(null);
    setFotoCedula(null);
  };

  const renderMenu = () => (
    <div className="main-content">
      <div className="page-header">
        <h1>Panel de Vigilancia</h1>
        <p>Control de acceso y registro de visitantes</p>
      </div>
      <div className="profile-card">
        <div className="profile-greeting">Vigilante en turno</div>
        <div className="profile-name">{user.nombre_completo}</div>
        <div className="profile-detail">
          <span>🛡️ Control de acceso activo</span>
        </div>
      </div>
      <div className="menu-grid">
        <button className="menu-item" onClick={() => { setView('escanear'); setEscaneoResult(null); setError(''); }}>
          <span className="menu-icon">📷</span>
          <span className="menu-label">Escanear QR</span>
          <span className="menu-desc">Lee el pase digital del visitante</span>
        </button>
        <button className="menu-item" onClick={() => { setView('visitas'); loadVisitas(); }}>
          <span className="menu-icon">📋</span>
          <span className="menu-label">Historial de Visitas</span>
          <span className="menu-desc">Todas las entradas registradas</span>
        </button>
        <button className="menu-item" onClick={() => setView('buscar')}>
          <span className="menu-icon">🔍</span>
          <span className="menu-label">Buscar Visitante</span>
          <span className="menu-desc">Consulta por número de cédula</span>
        </button>
      </div>
    </div>
  );

  const renderEscanear = () => (
    <div className="main-content">
      <div className="page-header flex-between">
        <div>
          <h1>Escanear Código QR</h1>
          <p>Apunte la cámara al código QR del visitante</p>
        </div>
        <button className="btn btn-outline" onClick={() => { setView('menu'); resetEscanear(); }}>Volver</button>
      </div>
      
      {/* Overlay de cámara para fotos */}
      {showCamera && (
        <div style={{
          position: 'fixed', top: 0, left: 0, width: '100vw', height: '100vh',
          background: '#000', zIndex: 9999, display: 'flex', flexDirection: 'column',
          alignItems: 'center', justifyContent: 'center'
        }}>
          <video ref={videoRef} autoPlay playsInline muted
            style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
          <canvas ref={canvasRef} style={{ display: 'none' }} />
          <div style={{
            position: 'absolute', bottom: 40, display: 'flex', gap: 20
          }}>
            <button className="btn btn-outline" onClick={closeCamera}
              style={{ background: 'rgba(255,255,255,0.15)', color: '#fff', border: '1px solid rgba(255,255,255,0.3)' }}>
              ❌ Cancelar
            </button>
            <button className="btn btn-accent" onClick={capturePhoto}
              style={{
                width: 70, height: 70, borderRadius: '50%', fontSize: '1.5rem',
                background: '#fff', color: '#000', border: '4px solid var(--primary)',
                display: 'flex', alignItems: 'center', justifyContent: 'center'
              }}>
              📷
            </button>
          </div>
          <div style={{
            position: 'absolute', top: 30, color: '#fff', fontSize: '1rem',
            background: 'rgba(0,0,0,0.5)', padding: '8px 20px', borderRadius: 20
          }}>
            {cameraTarget === 'placa' ? '🚗 Capture la placa del vehículo' : '🪪 Capture la cédula del visitante'}
          </div>
        </div>
      )}

      <div className="card" style={{ maxWidth: '580px' }}>
        {!escaneoResult ? (
          <>
            <div className="card-header"><h2>📷 Lector QR</h2></div>
            <QRScanner onScan={handleScanResult} />
          </>
        ) : escaneoResult.success ? (
          <div className="alert alert-success" style={{ margin: '16px 0' }}>
            ✅ {escaneoResult.success}
          </div>
        ) : (
          <>
            {/* Paso 1: Info del QR */}
            {paso === 'info' && (
              <>
                <div className="card-header"><h2>📋 Datos del QR escaneado</h2></div>
                <div className="qr-info">
                  {(escaneoResult.visitantes || [{ nombre: escaneoResult.visitante_nombre, cedula: escaneoResult.visitante_cedula }]).map((v, i) => (
                    <p key={i}><strong>👤 Visitante {i + 1}</strong> {v.nombre}<br/><span style={{fontSize:'0.78rem',color:'var(--text-secondary)'}}>🪪 {v.cedula}</span></p>
                  ))}
                  <p><strong>🚗 Vehículos</strong> {(() => { const p = escaneoResult.placas || (escaneoResult.placa_vehiculo ? [escaneoResult.placa_vehiculo] : []); return p.length > 0 ? p.join(', ') : 'N/A'; })()}</p>
                  <p><strong>📋 Motivo</strong> {escaneoResult.motivo}</p>
                  <p><strong>📅 Caduca</strong> {escaneoResult.fecha_caducidad}</p>
                  <p><strong>🏠 Residente</strong> {escaneoResult.residente_nombre}</p>
                  <p><strong>🚪 Apto</strong> {escaneoResult.apartamento} / P{escaneoResult.piso}</p>
                </div>
                <div className="btn-row mt-16">
                  <button className="btn btn-outline" onClick={resetEscanear}>Cancelar</button>
                  <button className="btn btn-primary" onClick={() => setPaso('placa')}>
                    Continuar ➡️
                  </button>
                </div>
              </>
            )}

            {/* Paso 2: Foto de placa (opcional) */}
            {paso === 'placa' && (
              <>
                <div className="card-header"><h2>🚗 Fotografía de la placa</h2><span className="badge badge-neutral">Opcional</span></div>
                <p className="text-sm mb-16">Capture una foto de la placa del vehículo del visitante.</p>
                {fotoPlaca ? (
                  <div style={{textAlign:'center',marginBottom:16}}>
                    <img src={fotoPlaca} alt="Placa" style={{maxWidth:'100%',maxHeight:200,borderRadius:'var(--radius-sm)',border:'2px solid var(--border)'}} />
                    <button className="btn btn-ghost btn-sm mt-8" onClick={() => setFotoPlaca(null)}>🗑️ Eliminar foto</button>
                  </div>
                ) : (
                  <div className="mb-16">
                    <button className="btn btn-outline btn-full" onClick={() => openCamera('placa')}>
                      📸 Tomar foto de la placa
                    </button>
                  </div>
                )}
                <div className="btn-row mt-16">
                  <button className="btn btn-outline" onClick={() => setPaso('info')}>⬅ Atrás</button>
                  <button className="btn btn-primary" onClick={() => setPaso('cedula')}>
                    {fotoPlaca ? 'Continuar ➡️' : 'Omitir ➡️'}
                  </button>
                </div>
              </>
            )}

            {/* Paso 3: Foto de cédula (opcional) */}
            {paso === 'cedula' && (
              <>
                <div className="card-header"><h2>🪪 Fotografía de la cédula</h2><span className="badge badge-neutral">Opcional</span></div>
                <p className="text-sm mb-16">Capture una foto de la cédula de identidad del visitante.</p>
                {fotoCedula ? (
                  <div style={{textAlign:'center',marginBottom:16}}>
                    <img src={fotoCedula} alt="Cédula" style={{maxWidth:'100%',maxHeight:200,borderRadius:'var(--radius-sm)',border:'2px solid var(--border)'}} />
                    <button className="btn btn-ghost btn-sm mt-8" onClick={() => setFotoCedula(null)}>🗑️ Eliminar foto</button>
                  </div>
                ) : (
                  <div className="mb-16">
                    <button className="btn btn-outline btn-full" onClick={() => openCamera('cedula')}>
                      📸 Tomar foto de la cédula
                    </button>
                  </div>
                )}
                <div className="btn-row mt-16">
                  <button className="btn btn-outline" onClick={() => setPaso('placa')}>⬅ Atrás</button>
                  <button className="btn btn-success" onClick={confirmarVisita} disabled={loading}>
                    {loading ? 'Registrando...' : '✅ Confirmar Entrada'}
                  </button>
                </div>
              </>
            )}
          </>
        )}
        {error && <div className="alert alert-error mt-16">⚠️ {error}</div>}
      </div>
    </div>
  );

  const renderVisitasList = () => (
    <div className="main-content">
      <div className="page-header flex-between">
        <div>
          <h1>Historial de Visitas</h1>
          <p>{visitas.length} registros de entrada</p>
        </div>
        <button className="btn btn-outline" onClick={() => setView('menu')}>Volver</button>
      </div>
      {loading && <div className="spinner" />}
      {error && <div className="alert alert-error">⚠️ {error}</div>}
      {!loading && visitas.length === 0 && (
        <div className="empty-state">
          <div className="empty-icon">📋</div>
          <h3>Sin registros</h3>
          <p>No hay visitas registradas aún</p>
        </div>
      )}
      {visitas.map((v) => (
        <VisitItem key={v.id} visita={v} showReport onReport={openReport} reports={reportesMap[v.id] || []} />
      ))}
    </div>
  );

  const renderBuscar = () => (
    <div className="main-content">
      <div className="page-header flex-between">
        <div>
          <h1>Buscar Visitante</h1>
          <p>Consulta por número de cédula</p>
        </div>
        <button className="btn btn-outline" onClick={() => setView('menu')}>Volver</button>
      </div>
      <div className="card" style={{ maxWidth: '520px' }}>
        <div className="card-header">
          <h2>🔍 Búsqueda</h2>
        </div>
        <form onSubmit={handleSearch}>
          <div className="form-group">
            <label>Cédula del visitante</label>
            <input
              type="text"
              value={searchCedula}
              onChange={(e) => setSearchCedula(e.target.value)}
              placeholder="Ej: 123456789"
            />
          </div>
          <button type="submit" className="btn btn-primary btn-full">Buscar</button>
        </form>
      </div>
      {visitas.length > 0 && (
        <div className="mt-24">
          <h3 className="mb-16" style={{color:'var(--text-secondary)', fontSize:'0.85rem'}}>Resultados ({visitas.length})</h3>
          {visitas.map((v) => (
            <div key={v.id} className="history-item">
              <div className="visit-header">
                <div className="visit-name">{v.visitante_nombre}</div>
                <span className="badge badge-info">{v.visitante_cedula}</span>
              </div>
              <div className="visit-detail">
                <span>🏠 Apto {v.apartamento} P{v.piso} — {v.residente_nombre}</span>
                <span>📋 {v.motivo}</span>
              </div>
              <div className="visit-date">📅 {v.escaneadoEn?.toDate ? new Date(v.escaneadoEn.toDate()).toLocaleString('es-CO') : new Date(v.escaneado_en).toLocaleString('es-CO')}</div>
            </div>
          ))}
        </div>
      )}
    </div>
  );

  return (
    <>
      <Header />
      {view === 'menu' && renderMenu()}
      {view === 'escanear' && renderEscanear()}
      {view === 'visitas' && renderVisitasList()}
      {view === 'buscar' && renderBuscar()}

      {/* Modal de reporte */}
      {showReportModal && (
        <div style={{
          position: 'fixed', top: 0, left: 0, width: '100vw', height: '100vh',
          background: 'rgba(0,0,0,0.7)', zIndex: 9999, display: 'flex',
          alignItems: 'center', justifyContent: 'center', padding: 20
        }} onClick={() => setShowReportModal(false)}>
          <div className="card" style={{ maxWidth: 520, width: '100%', maxHeight: '90vh', overflow: 'auto' }}
            onClick={(e) => e.stopPropagation()}>
            <div className="card-header">
              <h2>🚩 Reportar Anomalía</h2>
            </div>
            {reportVisita && (
              <div className="mb-16" style={{fontSize:'0.82rem',color:'var(--text-secondary)'}}>
                <p>Visita de: {(reportVisita.visitantes || [{nombre: reportVisita.visitante_nombre}])[0]?.nombre}</p>
                <p>Apto {reportVisita.apartamento} P{reportVisita.piso} — {reportVisita.residente_nombre}</p>
                <p>📅 {reportVisita.escaneadoEn?.toDate ? new Date(reportVisita.escaneadoEn.toDate()).toLocaleString('es-CO') : ''}</p>
              </div>
            )}
            <div className="form-group">
              <label>Descripción de la anomalía</label>
              <textarea value={reportText} onChange={(e) => setReportText(e.target.value)}
                placeholder="Describe la situación anómala detectada..."
                rows={4} style={{ width: '100%', borderRadius: 'var(--radius-sm)', padding: 12, border: '1px solid var(--border)', fontFamily: 'inherit' }} />
            </div>
            <div className="mb-16">
              <label style={{fontWeight:700,fontSize:'0.78rem',color:'var(--text-secondary)',textTransform:'uppercase',letterSpacing:'0.8px',marginBottom:8,display:'block'}}>
                📸 Fotos del reporte
              </label>
              {reportFotos.length > 0 && (
                <div style={{display:'flex',gap:8,flexWrap:'wrap',marginBottom:10}}>
                  {reportFotos.map((f, i) => (
                    <div key={i} style={{position:'relative'}}>
                      <img src={f} alt={`Reporte ${i+1}`} style={{width:72,height:72,borderRadius:8,objectFit:'cover',border:'2px solid var(--border)'}} />
                      <button className="btn btn-ghost btn-sm" onClick={() => setReportFotos(reportFotos.filter((_,j) => j!==i))}
                        style={{position:'absolute',top:-6,right:-6,background:'var(--danger)',color:'#fff',borderRadius:'50%',width:22,height:22,fontSize:'0.6rem',padding:0,display:'flex',alignItems:'center',justifyContent:'center'}}>✕</button>
                    </div>
                  ))}
                </div>
              )}
              <input ref={reportFileRef} type="file" accept="image/*" capture="environment" style={{display:'none'}}
                onChange={handleReportFoto} />
              <button className="btn btn-outline btn-full" onClick={() => reportFileRef.current?.click()}>
                📎 Adjuntar foto
              </button>
            </div>
            {error && <div className="alert alert-error">⚠️ {error}</div>}
            <div className="btn-row mt-16">
              <button className="btn btn-outline" onClick={() => setShowReportModal(false)}>Cancelar</button>
              <button className="btn btn-danger" onClick={submitReport} disabled={reportLoading}>
                {reportLoading ? 'Enviando...' : '🚩 Enviar Reporte'}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}